import type { CanonicalSearchLoad } from '@numeo/types'
/**
 * Agentic Hub event replay handler.
 *
 * When the socket reconnects, this module is called by webSocketMiddleware. For each active tab
 * it fetches missed events from socket-service's replay endpoint (/v1/replay) and dispatches
 * them through the normal AgenticHub handler pipeline, so the UI catches up without a refresh.
 *
 * If the cursor is too old (stale: true from server), it falls back to the existing HTTP refresh
 * endpoints — same behaviour as a manual page refresh.
 */
import { AgenticHubPayload } from '@numeo/types'
import { WsMessage, WsMessageCategory } from '@numeo/types-core'
import { socketServiceAxios } from 'setup/SetupAxios'
import { AppDispatch, RootState } from 'store'
import * as agenticHubApi from '../services/agenticHubApi'
import { parseRawRequests } from '../utils/parse-requests'
import { AgentRequest, setLoads, setLoadsLoading, setPendingRequests } from './agenticHubSlice'
import { handleAgenticHubSocketEvent } from './socket-handlers'

interface ReplayEvent {
    eventId: string
    timestamp: number
    action: string
    message: WsMessage
}

interface ReplayResponse {
    events: ReplayEvent[]
    stale: boolean
}

// Guard against concurrent replay runs. Rapid reconnect cycles (flaky Wi-Fi) would otherwise
// spawn overlapping /v1/replay requests for every tab. The monotonic cursor in the slice
// prevents state regression, but the extra HTTP traffic is pure waste.
let replayInFlight = false

/**
 * Fetch and dispatch missed events for a single tab.
 * Returns { eventCount, stale } so the caller can decide whether to trigger the stale fallback.
 */
async function replayForTab(dispatch: AppDispatch, tabId: string, afterEventId: string | undefined): Promise<{ eventCount: number; stale: boolean }> {
    try {
        const res = await socketServiceAxios.get<ReplayResponse>('/v1/replay', {
            params: {
                tabId,
                ...(afterEventId ? { afterEventId } : {}),
            },
        })
        const { events, stale } = res.data

        for (const ev of events) {
            if (ev.message.categoryType !== WsMessageCategory.AgenticHub || !ev.message.data) continue
            try {
                handleAgenticHubSocketEvent(dispatch, ev.message.data as AgenticHubPayload, ev.eventId)
            } catch (err) {
                console.error('[Replay] Error dispatching replayed event', err, { eventId: ev.eventId, action: ev.action })
            }
        }

        return { eventCount: events.length, stale }
    } catch (err) {
        console.error('[Replay] Failed to fetch replay events', err, { tabId, afterEventId })
        return { eventCount: 0, stale: false }
    }
}

/**
 * Fallback when the client's cursor is older than the server-side buffer TTL.
 *
 * At this point we can't replay individual events — they've aged out of Redis. Instead we
 * re-fetch the tab's full current state (loads + pending requests) from general-agent-service
 * so the UI reflects the latest DB state. Exactly what a manual page refresh would do for this
 * tab, minus reloading the entire React tree.
 *
 * Errors are logged but not rethrown — replay is best-effort.
 */
async function refreshTabFromScratch(dispatch: AppDispatch, tabId: string): Promise<void> {
    type RefreshResponse = { data?: Array<Record<string, unknown>> }

    // Show the skeleton while this reconnect refetch is in flight, else an empty
    // tab flashes "no loads" instead of a loader.
    dispatch(setLoadsLoading({ tabId, loading: true }))
    try {
        // Parallel fetch with Promise.allSettled so a single endpoint failure doesn't wipe out the
        // other's data. Loads and pending requests are independent — if one fails we still refresh
        // the UI with whatever succeeded rather than leaving everything stale.
        const [loadsSettled, reqSettled] = await Promise.allSettled([
            agenticHubApi.fetchLoads(tabId) as Promise<RefreshResponse>,
            agenticHubApi.fetchActiveRequests(tabId) as Promise<RefreshResponse>,
        ])

        if (loadsSettled.status === 'fulfilled') {
            // The loads endpoint returns raw rows from general-agent-service — the Redux shape is
            // looser than CanonicalSearchLoad (many optional fields), so go via unknown to keep the types honest.
            const loads = Array.isArray(loadsSettled.value?.data) ? (loadsSettled.value.data as unknown as CanonicalSearchLoad[]) : []
            // Secondary refetch — don't clobber the table with an empty result (a
            // transient / race-loser response). The primary fetch owns genuine empty.
            if (loads.length > 0) dispatch(setLoads({ tabId, loads }))
        } else {
            console.error('[Replay] Stale fallback: fetchLoads failed', loadsSettled.reason, { tabId })
        }

        if (reqSettled.status === 'fulfilled') {
            const raw = Array.isArray(reqSettled.value?.data) ? reqSettled.value.data : []
            const requests: AgentRequest[] = parseRawRequests(raw)
            dispatch(setPendingRequests({ tabId, requests }))
        } else {
            console.error('[Replay] Stale fallback: fetchActiveRequests failed', reqSettled.reason, { tabId })
        }
    } finally {
        dispatch(setLoadsLoading({ tabId, loading: false }))
    }
}

/**
 * Replay missed events for all open tabs in the store.
 *
 * Called by webSocketMiddleware on reconnect. For each tab with a cursor:
 *   - if the server has events since the cursor → dispatch them in order
 *   - if the cursor is stale (buffer expired) → re-fetch full tab state via HTTP
 *   - if no cursor → skip (nothing to catch up on)
 */
export async function replayMissedEvents(dispatch: AppDispatch, state: RootState): Promise<void> {
    if (replayInFlight) {
        // Rapid reconnects would otherwise fire overlapping replay passes. Skip cleanly —
        // the in-flight pass already uses the latest cursor from the store.
        return
    }

    const tabs = state.agenticHub.tabs
    const tabIds = Object.keys(tabs)
    if (tabIds.length === 0) return

    replayInFlight = true
    try {
        // Promise.allSettled (not all): each tab's replay is independent — if one tab's replay
        // rejects we don't want to cancel the others' in-flight requests. Today both helpers
        // swallow their own errors so this is belt-and-suspenders, but it keeps the guarantee
        // stable if either helper is ever refactored to rethrow.
        await Promise.allSettled(
            tabIds.map(async (tabId) => {
                const lastEventId = tabs[tabId]?.lastEventId
                // No cursor = no missed events possible (fresh tab, or never received a buffered event).
                // Fetching would double-dispatch events the user is already seeing.
                if (!lastEventId) return

                const { stale } = await replayForTab(dispatch, tabId, lastEventId)
                if (stale) {
                    await refreshTabFromScratch(dispatch, tabId)
                }
            })
        )
    } finally {
        replayInFlight = false
    }
}
